<template>
  <div class="opple-config-platform">
    <TopBar
      :has-data="areaCard !== null"
      :is-delete-loading="isDeleteLoading"
      @showAreaDialog="showAreaDialog"
      @delete="onDelete"
    />
    <div class="opple-config-platform_list">
      <div v-if="areaCard === null" class="opple-config-platform_list-noData">
        <img :src="noData">
        <p>{{ $t('ifttt.noConfiguration') }}</p>
      </div>
      <template v-else>
        <div class="opple-config-platform_areaCard">
          <span>{{ $t('otatask.areaname') }}:</span> {{ areaCard.AreaName }}
        </div>
      </template>
    </div>
    <OppleAreaSelectDialog
      v-if="isAreaDialogVisible && parentGpNo"
      :title="$t('ifttt.selectObjects')"
      :visible="isAreaDialogVisible"
      :gp-no="parentGpNo"
      :device="device"
      @onClose="handlerClose"
      @onOk="onDialogOk"
    />
    <Awake
      :visible="isAwakeVisible"
      :is-loading-configuration="isLoadingConfiguration"
      @onOk="onAwakeOk"
      @onClose="onAwakeClose"
    />
  </div>
</template>

<script>
import TopBar from './TopBar.vue'
import OppleAreaSelectDialog from './OppleAreaSelectDialog.vue'
import Awake from './Awake.vue'
import noData from '@/assets/img/no-data.png'
import { SetConfigs } from '@/axios/IDevice.js'
import { clearDeviceTriggerDetailCache, fetchTriggerAbility, getTriggerList } from '@/utils/triggerAbility'
import { fetchIFtttListByTrigger, getIFtttList } from '@/utils/iftttListByTrigger'
import { BatchCreate, Delete } from '@/axios/IFttt.js'
import { FindUserAreaTree } from '@/axios/IArea'
import { getElfDefaultIfttt } from '@/components/OppleLightElf/utils'
import { alerterror } from '@/assets/js/common.js'
import { decodeAreaGpNoFromConfigValue, encodeAreaGpNoToConfigValue, findAreaNodeInTree, findGpDetail, getAreaGpNoLowBits, isSameAreaGpNo, resolveParentGpNo } from '../utils'

export default {
  name: 'OppleControlAreaPlatform',
  components: {
    TopBar,
    OppleAreaSelectDialog,
    Awake,
  },
  props: {
    device: {
      type: Object,
      default: () => ({}),
    },
    gpList: {
      type: Array,
      default: () => [],
    },
    areaConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      isAreaDialogVisible: false,
      isAwakeVisible: false,
      isDeleteLoading: false,
      isLoadingConfiguration: false,
      areaCard: null,
      areaDeviceData: null,
      triggerList: [],
      parentGpNo: null,
      noData,
    }
  },
  watch: {
    device() {
      this.init()
    },
    gpList: {
      handler() {
        this.refreshAreaCard()
      },
      deep: true,
    },
    areaConfig: {
      handler() {
        this.refreshAreaCard()
      },
      deep: true,
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    async init() {
      if (!this.device?.VDeviceId) return

      this.parentGpNo = resolveParentGpNo(this.device)
      await this.loadTriggerList()
      await this.refreshAreaCard()
      await this.handlerIFttt()
    },

    async refreshAreaCard() {
      await this.loadAreaFromGpList()
    },

    isResolvedAreaName(gpNo, areaName) {
      const lowBits = getAreaGpNoLowBits(gpNo)
      return !!areaName && String(areaName) !== String(lowBits)
    },

    async loadAreaFromGpList() {
      const { areaGpCondition } = this.areaConfig || {}
      if (!areaGpCondition) return

      const detail = findGpDetail(this.gpList, areaGpCondition)
      const gpNoLowBits = decodeAreaGpNoFromConfigValue(detail?.DefaultValue)

      if (gpNoLowBits == null || gpNoLowBits === 0) {
        if (!this.areaDeviceData) {
          this.areaCard = null
        }
        return
      }

      if (
        this.areaCard &&
        isSameAreaGpNo(this.areaCard.Gpno, gpNoLowBits) &&
        this.isResolvedAreaName(gpNoLowBits, this.areaCard.AreaName)
      ) {
        return
      }

      const treeNode = await this.findAreaTreeNode(gpNoLowBits)
      const areaName = treeNode?.GpName || treeNode?.label || treeNode?.name || ''

      this.areaCard = {
        Gpno: treeNode?.Gpno ?? gpNoLowBits,
        AreaName: areaName || String(gpNoLowBits),
      }
    },

    async findAreaTreeNode(gpNo) {
      try {
        const res = await FindUserAreaTree({})
        const tree = res?.Result || []
        return findAreaNodeInTree(tree, gpNo)
      } catch (e) {
        return null
      }
    },

    async loadTriggerList() {
      try {
        const res = await fetchTriggerAbility(this.device.VDeviceId)
        const triggerListRes = getTriggerList(res)
        const showTriggers = triggerListRes.filter(item => item.UI?.TriggerGroup && item.UI?.AllowUsersEditAction !== 1)

        const triggerList = []
        showTriggers.forEach(trigger => {
          (trigger.OperationType || []).forEach(operation => {
            triggerList.push({
              ...trigger,
              OperationName: operation.Name,
              OperationValue: operation.Value,
              EnumValue: (trigger.EnumValue || []).map(item => ({
                ...item,
                iftttList: [],
              })),
            })
          })
        })

        this.triggerList = triggerList
      } catch (e) {
        console.log('loadTriggerList error', e)
      }
    },

    async handlerIFttt() {
      if (!this.triggerList.length) return

      this.triggerList.forEach(item => {
        item.iftttList = []
      })

      const iftttRes = await fetchIFtttListByTrigger(this.device.VDeviceId, { ShowEntry: 1 })

      getIFtttList(iftttRes).forEach(ifttt => {
        (ifttt.TriggerList || []).forEach(triggerItem => {
          (triggerItem.TriggerList || []).forEach(trigger => {
            const triggerIfttt = this.triggerList.find(item => item.KeyID === trigger.KeyId && item.ExtraKeyId === trigger.ExtraKeyId && item.OperationValue === trigger.OperationType && item.MsgID === trigger.MsgId)

            if (!triggerIfttt) return

            if (!Array.isArray(triggerIfttt.iftttList)) {
              triggerIfttt.iftttList = []
            }
            triggerIfttt.iftttList.push(ifttt)
          })
        })
      })
    },

    async refreshIftttState() {
      clearDeviceTriggerDetailCache(this.device.VDeviceId)
      await this.handlerIFttt()
      await this.refreshAreaCard()
    },

    showAreaDialog() {
      this.isAreaDialogVisible = true
    },

    handlerClose() {
      this.isAreaDialogVisible = false
    },

    onDialogOk(data) {
      this.areaDeviceData = data
      this.handlerClose()
      this.isAwakeVisible = true
    },

    onAwakeClose() {
      this.isAwakeVisible = false
    },

    async onAwakeOk() {
      if (this.isLoadingConfiguration || !this.areaDeviceData) return

      const data = this.areaDeviceData
      if (data.type !== 'area') return

      this.isLoadingConfiguration = true

      try {
        const selectArea = data.selectArea
        const gpNo = selectArea.id
        const areaName = selectArea.label

        this.areaCard = {
          Gpno: gpNo,
          AreaName: areaName,
        }

        await this.setControlAreaConfig(gpNo)

        const deviceGpNos = (this.device.ParentGpnos || '').replace(/\@/g, '').split(',') || []
        let batchedGpNo = null

        if (selectArea.id !== this.device.Gpno && deviceGpNos.length === selectArea.Level) {
          batchedGpNo = Number(deviceGpNos[selectArea.Level - 1])
        }

        const iftttReqList = await getElfDefaultIfttt(
          this.device,
          {
            ActionType: 6,
            gpNo,
            areaName,
            batchedGpNo,
          },
          this.triggerList
        )

        const res = await BatchCreate(iftttReqList)

        if (res && res.StatusCode === 0 && Array.isArray(res.Result)) {
          const failData = res.Result.find(item => item.StatusCode !== 0)
          if (failData?.StatusCode) {
            alerterror(this, failData.StatusCode)
          }
        }

        this.isAwakeVisible = false
        this.areaDeviceData = null
        this.$emit('success')
        await this.refreshIftttState()
      } catch (e) {
        console.log('onAwakeOk error', e)
      } finally {
        this.isLoadingConfiguration = false
      }
    },

    async setControlAreaConfig(gpNo) {
      const { areaGpCondition } = this.areaConfig || {}
      if (!areaGpCondition) return

      const { GpNo, GpKeyId } = areaGpCondition
      const value = encodeAreaGpNoToConfigValue(gpNo)

      const res = await SetConfigs({
        IsForced: true,
        VDeviceId: this.device.VDeviceId,
        GpNo: String(GpNo),
        ConfigItems: [
          {
            ChildId: 65535,
            Key: Number(GpKeyId),
            Values: [value],
          },
        ],
      })

      if (res.StatusCode !== this.$store.state.rightStatusCode) {
        alerterror(this, res.StatusCode)
        throw new Error('SetConfigs failed')
      }

      const detail = findGpDetail(this.gpList, areaGpCondition)
      if (detail) {
        detail.DefaultValue = value
      }
    },

    async onDelete() {
      this.isDeleteLoading = true
      const iftttNos = []

      this.triggerList.forEach(item => {
        (item.iftttList || []).forEach(ifttt => {
          iftttNos.push(ifttt.IFT_ID)
        })
      })

      try {
        await Promise.all(iftttNos.map(no => Delete({ iftttNo: no })))
        await this.setControlAreaConfig(0)
        this.areaCard = null
        this.areaDeviceData = null
        this.$emit('success')
        await this.refreshIftttState()
      } catch (e) {
        console.log('onDelete error', e)
      } finally {
        this.isDeleteLoading = false
      }
    },
  },
}
</script>

<style lang="scss">
.opple-config-platform {
  flex: 1;

  &_list {
    display: flex;
    flex-wrap: wrap;
    padding-left: 10px;

    &-noData {
      margin: 0 auto;
      width: 280px;
      text-align: center;
      font-size: 12px;
      font-weight: 400;
      color: #9e9e9e;

      img {
        width: 100%;
        margin-bottom: 10px;
      }
    }
  }

  &_areaCard {
    min-width: 180px;
    padding: 10px 15px;
    border: 1px solid #dddfe5;
    border-radius: 2px;

    span {
      color: #9e9e9e;
    }
  }
}
</style>
